import {
  collection,
  doc,
  getDocsFromServer,
  limit,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
  writeBatch,
  type Timestamp,
  type QuerySnapshot,
} from "firebase/firestore";
import { getRestaurantFirestore } from "./config";
import { isRestaurantPersistentNotificationType } from "./notification-types";

export type RestaurantNotification = {
  id: string;
  title: string;
  body: string;
  type: string;
  data: Record<string, string>;
  read: boolean;
  createdAt: Timestamp | null;
};

export type NotificationFeedScope = { ownerId: string; storeId: string };

export type NotificationFeedSnapshotMetadata = {
  fromCache: boolean;
  hasPendingWrites: boolean;
  isBaseline?: boolean;
  latestCreatedAt?: Timestamp | null;
};

export type NotificationFeedAdapter = {
  subscribe: (
    scope: NotificationFeedScope,
    onItems: (items: RestaurantNotification[], metadata?: NotificationFeedSnapshotMetadata) => void,
    onError: (error: unknown) => void,
  ) => () => void;
  markRead: (notificationId: string) => Promise<void>;
  markAllRead: (notificationIds: readonly string[]) => Promise<void>;
};

export const firebaseNotificationFeedAdapter: NotificationFeedAdapter = {
  subscribe(scope, onItems, onError) {
    const db = getRestaurantFirestore();
    if (!db) {
      onError(new Error("Firestore is unavailable"));
      return () => undefined;
    }
    // This exact shape is backed by infra/a2z's
    // notifications_owner_store_created composite index. Security rules
    // enforce request.auth.uid == ownerId; storeId is a client query boundary,
    // so keeping that equality here is what prevents cross-store feed mixing.
    const feedQuery = query(
      collection(db, "notifications"),
      where("ownerId", "==", scope.ownerId),
      where("ownerType", "==", "SELLER"),
      where("storeId", "==", scope.storeId),
      orderBy("createdAt", "desc"),
      limit(50),
    );
    let active = true;
    let unsubscribe: (() => void) | undefined;
    const publish = (snapshot: QuerySnapshot, isBaseline = false) => {
      if (!active) return;
      const items = snapshot.docs.map((entry) => ({
        id: entry.id,
        ...(entry.data() as Omit<RestaurantNotification, "id">),
      }));
      // The query window includes every event type. Hidden historical rows
      // must still bound freshness when older actionable rows enter it later.
      let latestCreatedAt: Timestamp | null = null;
      for (const item of items) {
        const createdAt = item.createdAt;
        if (createdAt && Number.isFinite(createdAt.toMillis()) &&
          (!latestCreatedAt || createdAt.seconds > latestCreatedAt.seconds ||
            (createdAt.seconds === latestCreatedAt.seconds && createdAt.nanoseconds > latestCreatedAt.nanoseconds))) {
          latestCreatedAt = createdAt;
        }
      }
      onItems(
        items.filter((entry) => isRestaurantPersistentNotificationType(entry.type)),
        {
          fromCache: snapshot.metadata.fromCache,
          hasPendingWrites: snapshot.metadata.hasPendingWrites,
          isBaseline,
          latestCreatedAt,
        },
      );
    };
    const subscribe = () => {
      if (!active) return;
      unsubscribe = onSnapshot(
        feedQuery,
        { includeMetadataChanges: true },
        (snapshot) => publish(snapshot),
        (error) => { if (active) onError(error); },
      );
    };
    // A server read establishes history before the live listener starts, so
    // commits arriving in its first callback can still be recognized as new.
    void getDocsFromServer(feedQuery).then(
      (snapshot) => {
        if (!active) return;
        publish(snapshot, true);
        subscribe();
      },
      (error) => {
        if (!active) return;
        console.warn("[restaurant-notifications] Firestore baseline read failed", error);
        // Keep cache/feed visibility available. The first authoritative
        // listener snapshot will establish history silently after recovery.
        subscribe();
      },
    );
    return () => {
      active = false;
      unsubscribe?.();
    };
  },
  async markRead(notificationId) {
    const db = getRestaurantFirestore();
    if (!db) throw new Error("Firestore is unavailable");
    await updateDoc(doc(db, "notifications", notificationId), { read: true });
  },
  async markAllRead(notificationIds) {
    if (notificationIds.length === 0) return;
    const db = getRestaurantFirestore();
    if (!db) throw new Error("Firestore is unavailable");
    const batch = writeBatch(db);
    for (const notificationId of notificationIds) {
      batch.update(doc(db, "notifications", notificationId), { read: true });
    }
    await batch.commit();
  },
};
